/**
 * BlogTopicHub — /blog/topics/:slug
 *
 * The community entry point for a single topic. Combines:
 *   • Header describing the topic (icon, color, invitation blurb)
 *   • **Ask box** for the topic (posts to /questions/ask with topic pre-set)
 *   • Recent published articles under the topic
 *   • Popular questions under the topic
 *
 * Kept in the .blog cream/teal/Lora aesthetic. No fat SocialPulse chrome.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ListenToAnswer from "@/components/publisher/ListenToAnswer";
import ListenChip from "@/components/publisher/ListenChip";
import TransliterateInput from "@/components/TransliterateInput";
import BlogHeader from "@/components/blog/BlogHeader";
import {
  Loader2, Send, Sparkles, ArrowRight, MessageCircle, ThumbsUp, PenLine,
} from "lucide-react";

const PUB_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

// Curated per-topic identity — mirrors BlogSubpages.TOPIC_CARDS so
// the identity carries from the topics grid into each hub.
const TOPIC_IDENTITY = {
  health:               { label: "Health",              color: "#DC2626", tint: "#FEF2F2", blurb: "Real stories about diagnoses, doctors, recipes, and walks — writing that changes how people care for themselves." },
  education:            { label: "Education",           color: "#2563EB", tint: "#EFF6FF", blurb: "From village schools to global classrooms — a teacher, a course, a study hack, or a small experiment." },
  finance:              { label: "Finance",             color: "#F59E0B", tint: "#FFFBEB", blurb: "Money, savings, taxes, first jobs, tiny businesses. What did nobody tell you that you had to learn the hard way?" },
  food:                 { label: "Food",                color: "#EA580C", tint: "#FFF7ED", blurb: "Grandma's recipe, a street stall, a farm-to-table experiment. Food stories travel further than food itself." },
  travel:               { label: "Travel",              color: "#0284C7", tint: "#F0F9FF", blurb: "Not the guidebook. The moment. The neighbourhood. The person who changed your route." },
  nature:               { label: "Nature",              color: "#10B981", tint: "#ECFDF5", blurb: "A tree you know by name. A river you swim in. The season your city forgets." },
  science:              { label: "Science",             color: "#7C3AED", tint: "#F5F3FF", blurb: "Curious research, kitchen experiments, citizen astronomy. Explain something you love in plain language." },
  weather:              { label: "Weather",             color: "#0EA5E9", tint: "#F0F9FF", blurb: "Monsoons, blizzards, the day the sky went orange. Local weather memory is worth documenting." },
  farmers:              { label: "Farmers",             color: "#65A30D", tint: "#F7FEE7", blurb: "Notes from the field. Crop years, market prices, water, seeds, cooperatives. Farmers are journalists of the land." },
  alumni:               { label: "Alumni",              color: "#4F46E5", tint: "#EEF2FF", blurb: "Where did your batch scatter? What did school give you that you only understood later?" },
  "family-trees":       { label: "Family Trees",        color: "#166534", tint: "#F0FDF4", blurb: "One story per grandparent is a gift. Start the family record." },
  "family-heritage":    { label: "Family & Heritage",   color: "#B45309", tint: "#FFFBEB", blurb: "Traditions, rituals, festivals, dialects, family recipes. The culture inside your home is worth publishing." },
  memorials:            { label: "Memorials",           color: "#78350F", tint: "#FEFCE8", blurb: "A page for someone you loved. A memory that shouldn't fade." },
  "homes-real-estate":  { label: "Homes & Real Estate", color: "#7C2D12", tint: "#FEF3E4", blurb: "Renting, buying, building, restoring. Neighbourhood profiles. What the listings don't tell you." },
  community:            { label: "Community",           color: "#0F766E", tint: "#F0FDFA", blurb: "Local heroes, volunteer projects, neighbourhood repairs. Communities become visible when someone writes about them." },
  "local-news":         { label: "Local News",          color: "#334155", tint: "#F1F5F9", blurb: "Council meetings, potholes, market days, elections. Cover your street the way a national paper covers a capital." },
  "arts-culture":       { label: "Arts & Culture",      color: "#DB2777", tint: "#FDF2F8", blurb: "Local artists, tiny theatres, gigs, bookshops, murals. If it moved you, it deserves a page." },
  history:              { label: "History",             color: "#78350F", tint: "#FEF3E4", blurb: "A place, a person, a struggle. Recover a story that was almost lost." },
  "faith-spirituality": { label: "Faith & Spirituality",color: "#9333EA", tint: "#FAF5FF", blurb: "Prayer, doubt, practice, community. Faith stories are honest when they're specific." },
  technology:           { label: "Technology",          color: "#1E293B", tint: "#F8FAFC", blurb: "How you use a tool, what broke, what you built. Explain tech to your parents." },
  business:             { label: "Business",            color: "#0F766E", tint: "#F0FDFA", blurb: "Small business, side project, employer story, market changes." },
};

const DEFAULT_IDENTITY = {
  label: "Explore",
  color: "#0F766E",
  tint:  "#F0FDFA",
  blurb: "The community for this topic is just getting started — you can be the first voice.",
};


export default function BlogTopicHub() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const ident = useMemo(() => TOPIC_IDENTITY[slug] || { ...DEFAULT_IDENTITY, label: slug ? slug.replace(/-/g, " ") : "Explore" }, [slug]);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState(null);
  const [freshAnswer, setFreshAnswer] = useState(null);

  useEffect(() => {
    let alive = true;
    const cacheKey = `blog:hub:${slug}`;
    // Instant paint from sessionStorage if the /blog/topics grid preloaded it.
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        if (Date.now() - cached.ts < 5 * 60 * 1000 && Array.isArray(cached.articles)) {
          setArticles(cached.articles);
          setArticlesLoading(false);
        }
      }
    } catch { /* ignore */ }
    (async () => {
      try {
        const r = await fetch(`${PUB_BASE}/hub/topic/${encodeURIComponent(slug)}?limit=6`);
        if (r.ok) {
          const d = await r.json();
          const arts = d.articles || [];
          if (alive) setArticles(arts);
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), articles: arts }));
          } catch { /* quota — ignore */ }
        }
      } catch { /* noop */ }
      if (alive) setArticlesLoading(false);

      try {
        const r = await fetch(`${PUB_BASE}/questions?topic=${encodeURIComponent(slug)}&limit=10&sort=popular`);
        const d = await r.json();
        if (alive) setQuestions(d.questions || []);
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [slug]);

  const ask = async (e) => {
    e.preventDefault();
    if (q.trim().length < 6) return;
    setAsking(true);
    setAskError(null);
    setFreshAnswer(null);
    try {
      const r = await fetch(`${PUB_BASE}/questions/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: q.trim(), topic: slug }),
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const d = await r.json();
      setFreshAnswer(d);
      setQ("");
      // Refresh the list so the answer is visible immediately
      const list = await fetch(`${PUB_BASE}/questions?topic=${encodeURIComponent(slug)}&limit=10&sort=popular`);
      if (list.ok) {
        const data = await list.json();
        setQuestions(data.questions || []);
      }
    } catch (err) {
      setAskError(err.message || "Ask failed — please try again.");
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }} data-testid={`blog-topic-hub-${slug}`}>
      <BlogHeader activeNav="explore-topics" />

      {/* Hero */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 pb-6">
        <div className="flex items-start gap-4 md:gap-6">
          <div
            className="w-16 h-16 md:w-20 md:h-20 rounded-2xl grid place-items-center text-3xl shrink-0"
            style={{ background: ident.color, color: "#ffffff", boxShadow: `0 8px 24px ${ident.color}22` }}
            aria-hidden="true"
          >
            <span style={{ fontFamily: "'Lora', serif" }}>{ident.label.charAt(0)}</span>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.24em] mb-2" style={{ color: ident.color }}>
              Community
            </p>
            <h1 className="text-3xl sm:text-4xl leading-tight" style={{ fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: "-0.02em" }}>
              {ident.label}
            </h1>
            <p className="mt-2 text-slate-600 text-lg max-w-2xl">
              {ident.blurb}
            </p>
          </div>
        </div>
      </section>

      {/* Ask box */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-6" data-testid="blog-topic-hub-ask">
        <form
          onSubmit={ask}
          className="rounded-2xl border p-5 space-y-3"
          style={{ borderColor: `${ident.color}44`, background: ident.tint }}
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold" style={{ color: ident.color }}>
            <Sparkles className="w-3.5 h-3.5" />
            Ask the {ident.label} community
          </div>
          <TransliterateInput
            value={q}
            onChange={setQ}
            placeholder={`e.g. Ask anything about ${ident.label.toLowerCase()}…`}
            className="w-full px-3 py-2.5 pr-10 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
            defaultLang="off"
            data-testid="blog-topic-hub-ask-input"
            rows={2}
            maxLength={500}
            minLength={6}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-500">
              First-time answers are AI-generated. Repeat asks are free.
            </span>
            <button
              type="submit"
              disabled={asking || q.trim().length < 6}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold disabled:opacity-40"
              style={{ background: ident.color }}
              data-testid="blog-topic-hub-ask-submit"
            >
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Ask
            </button>
          </div>
          {askError && (
            <div className="text-xs text-red-600" data-testid="blog-topic-hub-ask-error">{askError}</div>
          )}
          {freshAnswer && (
            <div
              className="rounded-xl border bg-white p-4"
              style={{ borderColor: `${ident.color}44` }}
              data-testid="blog-topic-hub-fresh-answer"
            >
              <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: ident.color }}>
                {freshAnswer.cached ? "Cached answer" : "AI answer"}
              </div>
              <div className="mt-1 text-slate-900" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
                {freshAnswer.question}
              </div>
              <div className="mt-2 text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                {freshAnswer.answer}
              </div>
              <ListenToAnswer qid={freshAnswer.id} />
            </div>
          )}
        </form>
      </section>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pb-16 grid md:grid-cols-3 gap-8">
        {/* Articles column */}
        <section className="md:col-span-2" data-testid="blog-topic-hub-articles">
          <h2 className="text-2xl mb-4" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
            Latest in {ident.label}
          </h2>
          {articlesLoading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {!articlesLoading && articles.length === 0 && (
            <div className="rounded-2xl border p-6 bg-white text-center" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
              <PenLine className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600">
                No articles published under <strong>{ident.label}</strong> yet. Be the first — a single 200-word post is enough to open a community.
              </p>
              <button
                onClick={() => navigate(`/publisher/write?topic=${slug}`)}
                className="mt-4 inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-white"
                style={{ background: ident.color }}
                data-testid="blog-topic-hub-write-first"
              >
                Write the first ›
              </button>
            </div>
          )}
          <div className="space-y-3">
            {articles.map((a) => (
              <a
                key={a.id}
                href={`/blog/${a.slug}`}
                className="flex gap-4 items-start rounded-2xl border p-4 bg-white hover:shadow-md transition-shadow"
                style={{ borderColor: "rgba(15,23,42,0.06)" }}
                data-testid={`blog-topic-hub-article-${a.slug}`}
              >
                {a.cover_image_url && (
                  <img src={a.cover_image_url} alt="" className="w-24 h-24 rounded-lg object-cover shrink-0" loading="lazy" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-lg text-slate-900 leading-snug" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
                    {a.title}
                  </div>
                  {a.summary && (
                    <div className="mt-1 text-sm text-slate-600 line-clamp-2">{a.summary}</div>
                  )}
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    {a.author_display_name && (
                      <div className="text-xs text-slate-400">
                        By {a.author_display_name}
                      </div>
                    )}
                    <ListenChip slug={a.slug} testIdPrefix={`blog-topic-hub-listen-${a.slug}`} />
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 mt-1 shrink-0" />
              </a>
            ))}
          </div>
        </section>

        {/* Questions column */}
        <aside data-testid="blog-topic-hub-questions">
          <h2 className="text-2xl mb-4" style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}>
            Popular questions
          </h2>
          {questions.length === 0 ? (
            <div className="rounded-2xl border p-5 bg-white text-sm text-slate-500" style={{ borderColor: "rgba(15,23,42,0.06)" }}>
              No questions yet — use the box above to ask the first one.
            </div>
          ) : (
            <div className="space-y-2">
              {questions.map((qq) => (
                <a
                  key={qq.id}
                  href={`/blog/questions#${qq.id}`}
                  className="flex items-start gap-3 rounded-xl border p-3 bg-white hover:shadow-sm transition-shadow"
                  style={{ borderColor: "rgba(15,23,42,0.06)" }}
                  data-testid={`blog-topic-hub-question-${qq.id}`}
                >
                  <div className="flex flex-col items-center pt-0.5 shrink-0" style={{ color: ident.color }}>
                    <ThumbsUp className="w-3.5 h-3.5" />
                    <span className="text-[11px] font-bold">{qq.helpful_votes ?? 0}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 line-clamp-2">
                      {qq.question}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 line-clamp-2">
                      {qq.answer}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
          <a
            href="/blog/questions"
            className="mt-4 inline-flex items-center gap-1 text-xs font-semibold hover:underline"
            style={{ color: ident.color }}
            data-testid="blog-topic-hub-all-questions"
          >
            See all questions <ArrowRight className="w-3 h-3" />
          </a>
        </aside>
      </div>
    </div>
  );
}
